import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { T, card, cardTitle, tbl, th, td, btnPrimary, btnSecondary, alert as ALT, bandTokens } from '../lib/theme'
import * as XLSX from 'xlsx'

// Revenue codes for the two wallet types
const REV_STATE = '100000000001-10000001'
const REV_LGA   = '100000000001-10000002'

const fmt = (n) => '₦' + (Number(n)||0).toLocaleString(undefined,{maximumFractionDigits:0})

export default function Scoring({ session, roleCtx }) {
  const role       = roleCtx?.role || 'guest'
  const canUpload  = role === 'admin' || role === 'acsm_officer'
  const canScore   = role === 'admin' || role === 'acsm_officer'
  const [superAgents,   setSuperAgents]   = useState([])

  function rag(pct) {
    if (pct >= 100) return { color:'#3fb950', bg:'rgba(63,185,80,.15)',  border:'rgba(63,185,80,.3)',  label:'Target Met'    }
    if (pct >= 70)  return { color:'#E8A020', bg:'rgba(232,160,32,.15)', border:'rgba(232,160,32,.3)', label:'Near Target'   }
    return              { color:'#f85149', bg:'rgba(248,81,73,.12)',  border:'rgba(248,81,73,.25)', label:'Below Target'  }
  }
  const [agents,        setAgents]        = useState([])
  const [targets,       setTargets]       = useState({})   // {class: amount}
  const [weekEnd,       setWeekEnd]       = useState('')
  const [scores,        setScores]        = useState([])   // super_agent_scores rows
  const [overrides,     setOverrides]     = useState([])   // active target_overrides
  const [funding,       setFunding]       = useState([])   // wallet_funding rows for selected week
  const [loading,       setLoading]       = useState(true)
  const [uploading,     setUploading]     = useState(false)
  const [scoring,       setScoring]       = useState(false)
  const [msg,           setMsg]           = useState(null)
  const [failedRows,    setFailedRows]    = useState([])  // unknown agent ID rows
  const [filterWeek,    setFilterWeek]    = useState('')
  const [filterSA,      setFilterSA]      = useState('')
  const [season,        setSeason]        = useState('dry')

  const selS = { fontSize:12, fontFamily:T.font, background:T.surface3,
    border:`1px solid ${T.border}`, borderRadius:6, padding:'8px 12px',
    color:T.txtPrimary, cursor:'pointer' }

  const load = useCallback(async () => {
    const [
      { data:sa }, { data:ag }, { data:tg },
      { data:sc }, { data:se }, { data:ov }
    ] = await Promise.all([
      supabase.from('super_agents').select('*, lgas(name,tier,security_level)').eq('status','Active').order('company_name'),
      supabase.from('agents').select('id,agent_id,full_name,agent_class,super_agent_id,lgas(name)').eq('status','Active'),
      supabase.from('weekly_targets').select('*').order('effective_from',{ascending:false}).limit(10),
      supabase.from('super_agent_scores').select('*, super_agents(company_name,lgas(name))').order('week_ending',{ascending:false}).limit(2000),
      supabase.from('app_settings').select('*'),
      supabase.from('target_overrides').select('*').is('cleared_at', null),
    ])
    setSuperAgents(sa||[])
    setAgents(ag||[])
    // Build target map: pick latest per tier+class  {A:{1:amt,2:amt},B:{...},...}
    const tm = {}
    ;(tg||[]).forEach(t => {
      if (!t.tier) return
      if (!tm[t.tier]) tm[t.tier] = {}
      if (!tm[t.tier][t.agent_class]) tm[t.tier][t.agent_class] = t.dry_season_target
    })
    setTargets(tm)
    setScores(sc||[])
    setOverrides(ov||[])
    if (se) { const s=se.find(x=>x.key==='season'); if(s) setSeason(s.value) }
    if (sc&&sc[0]) {
      // Normalize week_ending to YYYY-MM-DD regardless of stored format (e.g. DD/MM/YYYY)
      const raw = sc[0].week_ending || ''
      let normalized = raw
      // Detect DD/MM/YYYY
      const ddmm = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (ddmm) normalized = `${ddmm[3]}-${ddmm[2]}-${ddmm[1]}`
      setFilterWeek(normalized)
    }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // Load funding rows whenever week changes
  useEffect(() => {
    if (!filterWeek) return
    supabase.from('wallet_funding').select('*').eq('week_ending', filterWeek)
      .then(({ data }) => setFunding(data||[]))
  }, [filterWeek])

  // ── Excel upload ─────────────────────────────────────────────────────────
  function handleExcelUpload(e) {
    const file = e.target.files[0]; if (!file) return
    e.target.value = ''   // allow re-upload same file
    setUploading(true); setMsg(null)
    const reader = new FileReader()
    reader.onload = async (evt) => {
      try {
        const wb   = XLSX.read(evt.target.result, { type:'binary' })
        const ws   = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws, { defval:'' })

        // Build agent lookup by agent_id (BIGINT stored as number — use String for matching)
        const agentMap = {}
        agents.forEach(a => { if(a.agent_id != null) agentMap[String(a.agent_id)] = a.id })

        const records  = []
        const badRows  = []  // rows with unrecognised Agent ID

        rows.forEach((r, idx) => {
          const aid_text  = String(r.agent_ID || r.agent_id || r['Agent ID'] || r['Customer ID'] || r.customer_id || '').trim()
          const aname     = String(r.agent_name || r['Agent Name'] || r['Customer Name'] || r.customer_name || '').trim()
          const amount    = Number(r.amount || r.Amount || r.amount_funded || r['Amount'] || 0)
          const rev_code  = String(r.revenue_code || r['Revenue Code'] || '').trim()
          const rev_name  = String(r.revenue_name || r['Revenue Name'] || '').trim()
          const rawDate   = r.payment_date || r['Payment Date'] || null
          let pay_date = null
          if (rawDate) {
            if (typeof rawDate === 'number') {
              // Excel serial date → JS Date (Excel epoch is 1899-12-30)
              const d = new Date(Math.round((rawDate - 25569) * 86400 * 1000))
              pay_date = d.toISOString().split('T')[0]
            } else {
              const d = new Date(rawDate)
              pay_date = isNaN(d) ? null : d.toISOString().split('T')[0]
            }
          }
          // week_ending: use explicit column if present, else derive as next Saturday from payment_date
          const rawWeek = r.week_ending || r['Week Ending'] || ''
          let week = ''
          if (rawWeek) {
            if (typeof rawWeek === 'number') {
              const d = new Date(Math.round((rawWeek - 25569) * 86400 * 1000))
              week = d.toISOString().split('T')[0]
            } else {
              const d = new Date(rawWeek)
              week = isNaN(d) ? String(rawWeek).trim() : d.toISOString().split('T')[0]
            }
          } else if (pay_date) {
            // Derive week ending as the Sunday on or after payment_date
            const d = new Date(pay_date)
            const day = d.getDay() // 0=Sun,1=Mon,...,6=Sat
            const daysToSun = day === 0 ? 0 : 7 - day
            d.setDate(d.getDate() + daysToSun)
            week = d.toISOString().split('T')[0]
          }
          if (!week || !amount) return

          const agent_uuid = agentMap[aid_text] || null
          if (aid_text && !agent_uuid) {
            badRows.push({ 'Row': idx+2, 'Agent ID': aid_text, 'Agent Name': aname,
              'Amount': amount, 'Week Ending': week, 'Error': 'Agent ID not found' })
            return  // ← skip inserting unmatched rows entirely
          }

          records.push({
            payment_date:  pay_date || null,
            agent_id_text: aid_text || null,
            agent_id:      agent_uuid,
            agent_name:    aname || null,
            amount_funded: amount,
            revenue_code:  rev_code || null,
            revenue_name:  rev_name || null,
            week_ending:   week,
          })
        })

        setFailedRows(badRows)

        if (!records.length) {
          setMsg({ type:'err', text:'No valid rows found. Check required columns: payment_date, agent_ID, agent_name, amount, revenue_code, revenue_name, week_ending' })
          setUploading(false); return
        }

        // Insert in batches
        let ok=0, fail=0
        let firstError = null
        for (let i=0; i<records.length; i+=100) {
          const { error } = await supabase.from('wallet_funding').insert(records.slice(i,i+100))
          if (error) { fail+=Math.min(100,records.length-i); if(!firstError) firstError=error }
          else ok+=Math.min(100,records.length-i)
        }

        const warnMsg = badRows.length ? ` ${badRows.length} row(s) had unknown Agent IDs — see below to download.` : ''
        const errDetail = firstError ? ` Error: ${firstError.message}` : ''
        setMsg({ type: fail?'err':'ok',
          text: `Uploaded ${ok} records${fail?`, ${fail} failed`:'.'}.${warnMsg}${errDetail}` })

        // Auto-refresh week filter to most recent uploaded week
        const weeks = [...new Set(records.map(r=>r.week_ending))].sort((a,b)=>b.localeCompare(a))
        if (weeks[0]) setFilterWeek(weeks[0])
        setUploading(false)
        load()
      } catch(err) {
        setMsg({ type:'err', text: err.message })
        setUploading(false)
      }
    }
    reader.readAsBinaryString(file)
  }

  // ── Auto-score: aggregate wallet funding per super-agent per week ─────────
  async function runScoring() {
    if (!filterWeek) { setMsg({ type:'err', text:'Select a week first.' }); return }
    setScoring(true); setMsg(null)

    // Pull all wallet funding for this week
    const { data: wf, error: wfErr } = await supabase.from('wallet_funding')
      .select('agent_id_text, agent_id, amount_funded, revenue_code, week_ending')
      .filter('week_ending', 'eq', filterWeek)

    if (!wf || !wf.length) {
      setMsg({ type:'err', text:'No wallet funding data found for this week.' })
      setScoring(false); return
    }

    // Sum state + LGA funding per agent (both revenue codes count)
    const agentFunding = {}
    wf.forEach(row => {
      const key = row.agent_id || row.agent_id_text || 'unknown'
      agentFunding[key] = (agentFunding[key] || 0) + (row.amount_funded || row.amount || 0)
    })

    // Group agents by super-agent
    const saFunding = {}   // superAgentId → { totalFunded, agentCount }
    agents.forEach(agent => {
      const saId = agent.super_agent_id
      if (!saId) return
      const fund = agentFunding[agent.id] || agentFunding[agent.agent_id] || 0
      if (!saFunding[saId]) saFunding[saId] = { totalFunded:0, agentCount:0 }
      saFunding[saId].totalFunded  += fund
      saFunding[saId].agentCount   += 1
    })

    const seasonFactor = season === 'rain' ? 0.75 : 1.0

    // Build upsert rows for super_agent_scores — skip banned/suspended super agents
    const scoreRows = []
    superAgents.filter(sa => sa.status === 'Active').forEach(sa => {
      const d          = saFunding[sa.id] || { totalFunded:0, agentCount:0 }
      // Use SA's own agent_class for target lookup; fall back to first linked agent's class, then '1'
      const saAgents   = agents.filter(a => a.super_agent_id === sa.id)
      const cls        = sa.agent_class || saAgents[0]?.agent_class || '1'
      const saTier     = sa.lgas?.tier || 'A'
      const secFactor  = (sa.lgas?.security_level || 'Green') === 'Red' ? 0.75 : 1.0
      const baseTarget = (targets[saTier]?.[cls] || targets['A']?.[cls] || 120000) * seasonFactor * secFactor
      const activeOv   = overrides.find(o => o.super_agent_id === sa.id)
      const weekTarget = activeOv ? activeOv.override_amount : baseTarget * (d.agentCount || 1)
      const pct        = weekTarget > 0 ? Math.round((d.totalFunded / weekTarget) * 100) : 0

      scoreRows.push({
        super_agent_id: sa.id,
        week_ending:    filterWeek,
        total_funded:   d.totalFunded,
        weekly_target:  weekTarget,
        agent_count:    d.agentCount,
        target_met:     d.totalFunded >= weekTarget,
        funding_pct:    pct,
      })
    })

    const { error } = await supabase.from('super_agent_scores')
      .upsert(scoreRows, { onConflict:'super_agent_id,week_ending' })

    if (error) {
      setMsg({ type:'err', text: error.message })
      setScoring(false)
      return
    }

    // ── Enforcement case auto-populate ──────────────────────────────────────
    // Fetch existing cases for this week to check for notes before auto-closing
    const { data: existingCases } = await supabase
      .from('enforcement_cases')
      .select('id, super_agent_id, status')
      .eq('week_ending', filterWeek)

    const existingMap = {}
    ;(existingCases || []).forEach(c => { existingMap[c.super_agent_id] = c })

    // Fetch which cases have notes (touched cases should not be auto-closed)
    const caseIdsWithNotes = new Set()
    if (existingCases && existingCases.length) {
      const { data: noteRows } = await supabase
        .from('case_notes')
        .select('case_id')
        .in('case_id', existingCases.map(c => c.id))
      ;(noteRows || []).forEach(n => caseIdsWithNotes.add(n.case_id))
    }

    const caseUpserts   = []  // cases to open / update
    const caseAutoClose = []  // case ids to auto-close (SA improved, no notes)

    scoreRows.forEach(row => {
      const pct = row.funding_pct
      const saId = row.super_agent_id

      if (pct <= 50) {
        // Below threshold — open or update case
        const severity = pct <= 25 ? 'at_risk' : 'watchlist'
        caseUpserts.push({
          super_agent_id: saId,
          week_ending:    filterWeek,
          funding_pct:    pct,
          weekly_target:  row.weekly_target,
          total_funded:   row.total_funded,
          severity,
          status:         existingMap[saId]?.status || 'open',  // keep existing status if already set
          updated_at:     new Date().toISOString(),
        })
      } else {
        // SA improved above 50% — auto-close only if no notes and was open
        const existing = existingMap[saId]
        if (existing && existing.status === 'open' && !caseIdsWithNotes.has(existing.id)) {
          caseAutoClose.push(existing.id)
        }
      }
    })

    // Upsert enforcement cases for SAs ≤50%
    if (caseUpserts.length) {
      await supabase.from('enforcement_cases')
        .upsert(caseUpserts, { onConflict:'super_agent_id,week_ending' })
    }

    // Auto-close untouched cases where SA improved
    if (caseAutoClose.length) {
      await supabase.from('enforcement_cases')
        .update({
          status:       'closed',
          closed_at:    new Date().toISOString(),
          closing_note: 'Auto-closed: SA funding improved above 50% threshold on re-run.',
          updated_at:   new Date().toISOString(),
        })
        .in('id', caseAutoClose)
    }
    // ── End enforcement auto-populate ────────────────────────────────────────

    const enfNote = caseUpserts.length
      ? ` · ${caseUpserts.length} enforcement case${caseUpserts.length!==1?'s':''} updated`
      : ''
    const closeNote = caseAutoClose.length
      ? ` · ${caseAutoClose.length} auto-closed`
      : ''

    setMsg({ type:'ok', text: `Scoring complete for ${scoreRows.length} Super Agents — week ending ${filterWeek}.${enfNote}${closeNote}` })
    load()
    setScoring(false)
  }

  // ── Leaderboard data ──────────────────────────────────────────────────────
  const weeks      = [...new Set(scores.map(s=>s.week_ending))].sort((a,b)=>b.localeCompare(a))
  const board      = scores
    .filter(s => s.week_ending === filterWeek && (!filterSA || s.super_agent_id === filterSA))
    .filter(s => superAgents.some(x => x.id === s.super_agent_id && x.status !== 'Banned' && x.status !== 'Suspended'))  // hide rows for banned/suspended SAs
    .sort((a,b) => b.funding_pct - a.funding_pct)

  function downloadScoreboard() {
    const rows = board.map((r,i) => ({
      'Rank':         i+1,
      'Super Agent':  r.super_agents?.company_name || '',
      'LGA':          r.super_agents?.lgas?.name || '',
      'Agents':       agents.filter(a => a.super_agent_id === r.super_agent_id).length,
      'Total Funded': r.total_funded || 0,
      'Weekly Target':r.weekly_target || 0,
      '% of Target':  r.funding_pct || 0,
      'Status':       r.target_met ? 'Target Met' : r.funding_pct >= 70 ? 'Near Target' : 'Below Target',
      'Week Ending':  filterWeek,
    }))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Scoreboard')
    XLSX.writeFile(wb, `SA_Scoreboard_${filterWeek}.xlsx`)
  }

  if (loading) return <div style={{ color:T.txtSecondary, fontSize:13 }}>Loading scoring…</div>

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:20, fontWeight:800, color:T.txtPrimary, marginBottom:4 }}>Scoring</div>
        <div style={{ fontSize:12, color:T.txtSecondary }}>
          Upload wallet funding · Auto-score per Super Agent · View weekly leaderboard
        </div>
      </div>

      {/* Role notice for view-only users */}
      {!canUpload && (
        <div style={{ ...ALT.info, marginBottom:16 }}>
          🔒 You have view-only access to scoring. Upload and Run Scoring are available to Admin and ACSM Officer users only.
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>

        {/* Upload + Score panel */}
        <div style={card}>
          <div style={cardTitle}>Upload Wallet Funding (Excel)</div>

          <div style={{ background:T.surface3, border:`1px solid ${T.border}`, borderRadius:8,
            padding:'12px 14px', marginBottom:14, fontSize:11, color:T.txtSecondary, lineHeight:1.8 }}>
            <strong style={{ color:T.txtPrimary }}>Required columns:</strong><br/>
            <code style={{ color:T.blue }}>payment_date · agent_ID · agent_name · amount · revenue_code · revenue_name · week_ending</code><br/>
            <strong style={{ color:T.txtPrimary }}>Revenue codes:</strong><br/>
            <span style={{ color:'#3fb950' }}>{REV_STATE}</span> — State Wallet Funding<br/>
            <span style={{ color:T.amber   }}>{REV_LGA}</span> — LGA Wallet Funding<br/>
            Both codes are summed per agent to get total funding.
          </div>

          {canUpload ? (
            <label style={{ border:`2px dashed ${T.border}`, borderRadius:10, padding:'24px',
              textAlign:'center', cursor:'pointer', fontSize:12, color:T.txtSecondary,
              display:'block', background:T.surface3, marginBottom:14 }}>
              {uploading ? '⏳ Uploading…' : '📂 Click to upload Excel file'}
              <input type="file" accept=".xlsx,.xls" style={{ display:'none' }}
                onChange={handleExcelUpload} disabled={uploading} />
            </label>
          ) : (
            <div style={{ border:`2px dashed ${T.border}`, borderRadius:10, padding:'24px',
              textAlign:'center', fontSize:12, color:T.txtMuted,
              background:T.surface3, marginBottom:14, userSelect:'none' }}>
              🔒 Upload restricted to Admin / ACSM Officer
            </div>
          )}

          {msg && <div style={msg.type==='ok' ? ALT.ok : ALT.err}>{msg.text}</div>}

          {failedRows.length > 0 && (
            <div style={{ marginTop:12, background:'rgba(248,81,73,.06)',
              border:'1px solid rgba(248,81,73,.25)', borderRadius:8, padding:'12px 14px' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#f85149' }}>
                  ⚠ {failedRows.length} row{failedRows.length!==1?'s':''} with unknown Agent IDs
                </div>
                <button style={{ ...btnSecondary, padding:'4px 12px', fontSize:11 }}
                  onClick={() => {
                    const wb = XLSX.utils.book_new()
                    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(failedRows), 'Failed Rows')
                    XLSX.writeFile(wb, 'failed_agent_ids.xlsx')
                  }}>
                  ⬇ Download Failed Rows
                </button>
              </div>
              <div style={{ maxHeight:120, overflowY:'auto', fontSize:11, color:'#f85149' }}>
                {failedRows.slice(0,8).map((r,i) => (
                  <div key={i} style={{ borderBottom:'1px solid rgba(248,81,73,.15)', padding:'3px 0' }}>
                    Row {r['Row']}: Agent ID <code style={{ background:'rgba(248,81,73,.1)', padding:'1px 4px', borderRadius:3 }}>{r['Agent ID']}</code> — not found
                  </div>
                ))}
                {failedRows.length > 8 && <div style={{ color:'rgba(248,81,73,.7)', marginTop:4 }}>…and {failedRows.length-8} more — download for full list</div>}
              </div>
            </div>
          )}
        </div>

        {/* Auto-score panel */}
        <div style={card}>
          <div style={cardTitle}>Auto-Score Super Agents</div>
          <div style={{ fontSize:11, color:T.txtSecondary, lineHeight:1.8, marginBottom:14 }}>
            After uploading wallet funding, select the week and click <strong style={{ color:T.txtPrimary }}>Run Scoring</strong>. The system will:<br/>
            <span style={{ color:'#3fb950' }}>✓</span> Sum State + LGA wallet funding per agent<br/>
            <span style={{ color:'#3fb950' }}>✓</span> Aggregate totals per Super Agent<br/>
            <span style={{ color:'#3fb950' }}>✓</span> Compare against weekly target × agent count<br/>
            <span style={{ color:'#3fb950' }}>✓</span> Apply {season==='rain'?'−25% rainy season adjustment':'dry season full targets'}<br/>
            <span style={{ color:'#3fb950' }}>✓</span> Mark Green (target met) or Red (below target)<br/>
            <span style={{ color:'#E8A020' }}>⚠</span> Auto-create Enforcement cases for SAs ≤50%
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:10, fontWeight:600, color:T.txtMuted, letterSpacing:'.06em',
              textTransform:'uppercase', display:'block', marginBottom:4 }}>WEEK ENDING</label>
            <input type="date" value={filterWeek} onChange={e=>setFilterWeek(e.target.value)}
              style={{ width:'100%', padding:'9px 12px', background:T.surface3,
                border:`1px solid ${T.border}`, borderRadius:6, fontSize:12,
                fontFamily:T.font, color:T.txtPrimary, outline:'none' }} />
          </div>

          <div style={{ fontSize:11, color:T.txtSecondary, marginBottom:14 }}>
            Season: <strong style={{ color: season==='dry'?T.amber:T.blue }}>
              {season==='dry'?'☀ Dry — full targets':'🌧 Rainy — −25%'}</strong>
            &nbsp;·&nbsp; {superAgents.length} Super Agents · {agents.length} Active Agents
          </div>

          <button style={{ ...btnPrimary, width:'100%', padding:12, fontSize:13,
            opacity: canScore ? 1 : 0.45, cursor: canScore ? 'pointer' : 'not-allowed' }}
            onClick={canScore ? runScoring : undefined}
            disabled={scoring || !filterWeek || !canScore}>
            {scoring ? '⏳ Calculating…' : canScore ? '▶ Run Scoring for Selected Week' : '🔒 Run Scoring — Admin / ACSM Officer only'}
          </button>
        </div>
      </div>

      {/* Leaderboard */}
      <div style={card}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:10 }}>
          <div style={cardTitle}>Super Agent Score Board</div>
          <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
            <select style={{ fontSize:12, background:T.surface3, border:`1px solid ${T.border}`,
              borderRadius:6, padding:'6px 10px', fontFamily:T.font, color:T.txtPrimary }}
              value={filterWeek} onChange={e=>setFilterWeek(e.target.value)}>
              {weeks.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
            <select style={{ fontSize:12, background:T.surface3, border:`1px solid ${T.border}`,
              borderRadius:6, padding:'6px 10px', fontFamily:T.font, color:T.txtPrimary }}
              value={filterSA} onChange={e=>setFilterSA(e.target.value)}>
              <option value="">All Super Agents</option>
              {superAgents.map(sa => <option key={sa.id} value={sa.id}>{sa.company_name}</option>)}
            </select>
            <button
              style={{ ...btnSecondary, padding:'6px 14px', fontSize:11, display:'flex', alignItems:'center', gap:6 }}
              onClick={downloadScoreboard}
              disabled={board.length === 0}>
              ⬇ Export Excel
            </button>
          </div>
        </div>

        {board.some(r => superAgents.some(x=>x.id===r.super_agent_id && x.status !== 'Banned' && x.status !== 'Suspended') && agents.filter(a=>a.super_agent_id===r.super_agent_id).length !== r.agent_count) && (
          <div style={{ padding:'9px 14px', borderRadius:6, marginBottom:10, fontSize:11,
            background:'rgba(248,81,73,.1)', border:'1px solid rgba(248,81,73,.25)', color:'#f85149' }}>
            ⚠ Agent count has changed since scoring was last run — <strong>re-run scoring</strong> for this week to update targets and percentages.
          </div>
        )}
        <table style={tbl}>
          <thead><tr>
            {['#','Super Agent','LGA','Agents','Total Funded','Weekly Target','% of Target','Status'].map(h=>
              <th key={h} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {board.map((r,i) => {
              const met        = r.target_met
              const pct        = r.funding_pct || 0
              const sa         = superAgents.find(s => s.id === r.super_agent_id)
              const liveCount  = agents.filter(a => a.super_agent_id === r.super_agent_id).length
              const stale      = liveCount !== r.agent_count
              const dangerous  = (sa?.lgas?.security_level || 'Green') === 'Red'
              return (
                <tr key={r.id}>
                  <td style={{ ...td, fontWeight:800, color: i<3?T.amber:T.txtMuted, width:28 }}>{i+1}</td>
                  <td style={{ ...td, fontWeight:600 }}>{r.super_agents?.company_name}</td>
                  <td style={{ ...td, color:T.txtSecondary }}>
                    {r.super_agents?.lgas?.name||'—'}
                    {dangerous && (
                      <span title="LGA has Dangerous security level — target reduced 25%"
                        style={{ marginLeft:5, fontSize:9, color:'#f85149', fontWeight:700,
                          background:'rgba(248,81,73,.12)', border:'1px solid rgba(248,81,73,.3)',
                          borderRadius:4, padding:'1px 4px', cursor:'help' }}>
                        ⚠ DANGER
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, color: stale?'#f85149':T.txtSecondary }}>
                    {liveCount}
                    {stale && <span title={`Score was run with ${r.agent_count} agents — re-run scoring to update`}
                      style={{ marginLeft:5, fontSize:9, color:'#f85149', cursor:'help' }}>⚠ re-run</span>}
                  </td>
                  <td style={{ ...td, fontWeight:700, color:rag(pct).color }}>{fmt(r.total_funded)}</td>
                  <td style={{ ...td, color:T.txtSecondary }}>{fmt(r.weekly_target)}</td>
                  <td style={{ ...td }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ flex:1, height:6, background:T.surface3, borderRadius:3, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${Math.min(pct,100)}%`,
                          background: rag(pct).color, borderRadius:3 }} />
                      </div>
                      <span style={{ fontSize:12, fontWeight:700,
                        color: rag(pct).color, minWidth:36 }}>{pct}%</span>
                    </div>
                  </td>
                  <td style={td}>
                    <span style={{
                      display:'inline-block', padding:'3px 10px', borderRadius:20,
                      fontSize:10, fontWeight:700, letterSpacing:'.04em',
                      background: rag(pct).bg,
                      color:       rag(pct).color,
                      border:      `1px solid ${rag(pct).border}`,
                    }}>
                      {rag(pct).label}
                    </span>
                  </td>
                </tr>
              )
            })}
            {board.length===0 && (
              <tr><td colSpan={8} style={{ ...td, textAlign:'center', color:T.txtMuted }}>
                No scores yet. Upload wallet funding then click Run Scoring.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
